import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { CountryViewStore } from "../../store";
import { CountryView } from "../view/CountryView";
import { CountryUpdateStore } from "../../store";
import { CountryUpdate } from "../update/CountryUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type CountryModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const CountryModalContent: React.FC<CountryModalContentProps> = observer(
  ({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new CountryUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new CountryViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <CountryView countryViewStore={storeInfo.store as CountryViewStore} />
        )}
        {storeInfo.type === "update" && (
          <CountryUpdate
            countryUpdateStore={storeInfo.store as CountryUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  }
);
